import React, { createContext, useContext, useState } from 'react';
import AchievementNotification from '../components/AchievementNotification';

const AchievementContext = createContext();

export const useAchievements = () => {
  const context = useContext(AchievementContext);
  if (!context) {
    throw new Error('useAchievements must be used within an AchievementProvider');
  }
  return context;
};

export const AchievementProvider = ({ children }) => {
  const [currentAchievement, setCurrentAchievement] = useState(null);
  const [, setAchievementQueue] = useState([]);
  const [isShowingAchievement, setIsShowingAchievement] = useState(false);

  const showAchievement = (achievement) => {
    if (currentAchievement) {
      // If there's already an achievement showing, queue this one
      setAchievementQueue(prev => [...prev, achievement]);
    } else {
      // Show immediately
      setCurrentAchievement(achievement);
      setIsShowingAchievement(true);
    }
  };

  const hideCurrentAchievement = () => {
    setCurrentAchievement(null);
    setIsShowingAchievement(false);
    
    // Show next achievement in queue if any - use a delay to ensure smooth transitions
    setTimeout(() => {
      setAchievementQueue(prev => {
        if (prev.length > 0) {
          const nextAchievement = prev[0];
          setCurrentAchievement(nextAchievement);
          setIsShowingAchievement(true);
          return prev.slice(1);
        }
        return prev;
      });
    }, 500); // Short delay to ensure smooth transitions
  };

  const handleApiResponse = (response) => {
    // Check if the API response contains new achievements
    if (response && response.newAchievements && response.newAchievements.length > 0) {
      // Process achievements one by one with a small delay to ensure proper queuing
      response.newAchievements.forEach((achievement, index) => {
        setTimeout(() => {
          showAchievement(achievement);
        }, index * 100); // 100ms delay between each achievement
      });
    }
    
    // Check if stats should be refreshed
    if (response && response.refreshStats) {
      window.dispatchEvent(new CustomEvent('statsRefresh'));
    }
  };

  return (
    <AchievementContext.Provider value={{ 
      showAchievement, 
      handleApiResponse,
      currentAchievement,
      isShowingAchievement
    }}>
      {children}
      <AchievementNotification 
        achievement={currentAchievement} 
        onClose={hideCurrentAchievement}
      />
    </AchievementContext.Provider>
  );
};

export default AchievementProvider;
